/**
 * Staff Clients Page - Olympic Fit Wellness
 * Connected to live Supabase backend
 */
import { useState, useEffect } from 'react';
import { 
  Users, 
  Search,
  Phone,
  Mail,
  Calendar,
  ChevronRight,
  Loader2
} from 'lucide-react';
import api from '../../lib/api';

export default function StaffClientsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const bookingsData = await api.getStaffBookings().catch(() => []);
      
      // Extract unique clients from bookings
      const clientMap = new Map();
      bookingsData.forEach(booking => {
        const clientId = booking.user_id;
        if (clientId && !clientMap.has(clientId)) {
          clientMap.set(clientId, {
            id: clientId,
            name: booking.profiles?.full_name || 'Member',
            email: booking.profiles?.email || 'N/A',
            phone: booking.profiles?.phone || '(469) 555-0000',
            sessions: 1,
            nextSession: booking.class_instances?.start_time || null,
            goal: booking.class_instances?.classes?.category || 'General Fitness'
          });
        } else if (clientId && clientMap.has(clientId)) {
          const existing = clientMap.get(clientId);
          existing.sessions += 1;
        }
      });
      
      const processedClients = Array.from(clientMap.values());
      setClients(processedClients.length > 0 ? processedClients : fallbackClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients(fallbackClients);
    } finally {
      setLoading(false);
    }
  };

  // Fallback mock data
  const fallbackClients = [
    { id: 'client_001', name: 'Sarah Johnson', email: 'sarah@email.com', phone: '(469) 555-1234', sessions: 24, nextSession: '2026-02-11', goal: 'Weight Loss' },
    { id: 'client_002', name: 'Mike Chen', email: 'mike@email.com', phone: '(469) 555-2345', sessions: 18, nextSession: '2026-02-12', goal: 'Muscle Building' },
    { id: 'client_003', name: 'Emily Davis', email: 'emily@email.com', phone: '(469) 555-3456', sessions: 36, nextSession: '2026-02-11', goal: 'General Fitness' },
    { id: 'client_004', name: 'James Wilson', email: 'james@email.com', phone: '(469) 555-4567', sessions: 12, nextSession: '2026-02-13', goal: 'Marathon Training' },
    { id: 'client_005', name: 'Lisa Anderson', email: 'lisa@email.com', phone: '(469) 555-5678', sessions: 42, nextSession: '2026-02-14', goal: 'Strength Training' }
  ];

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-clients">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-neutral-900">My Clients</h1>
          <p className="text-neutral-500">Manage your personal training clients</p>
        </div>
        <button className="inline-flex items-center justify-center gap-2 bg-green-600 text-white font-semibold px-6 py-3 text-sm tracking-wider hover:bg-green-700 transition-colors">
          ADD CLIENT
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-green-500"
          />
        </div>
      </div>

      {/* Clients List */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-neutral-100">
          <p className="text-sm text-neutral-500">
            Showing <span className="font-medium text-neutral-900">{filteredClients.length}</span> clients
          </p>
        </div>
        
        <div className="divide-y divide-neutral-100">
          {filteredClients.map((client) => (
            <div key={client.id} className="p-4 flex items-center gap-4 hover:bg-neutral-50 transition-colors cursor-pointer">
              <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center">
                <span className="text-white font-medium">
                  {client.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-neutral-900">{client.name}</h3>
                <div className="flex items-center gap-4 text-sm text-neutral-500 mt-1">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {client.email}
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {client.phone}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-neutral-500">{client.sessions} sessions</p>
                <p className="text-sm text-green-600">{client.goal}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-neutral-500">Next session</p>
                <p className="text-sm font-medium">{new Date(client.nextSession).toLocaleDateString()}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-neutral-400" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
